import React, { useRef } from 'react';
import { User, Mail, Upload, ExternalLink, Image as ImageIcon, HelpCircle } from 'lucide-react';
import { UserData } from '../types';

interface CertificateFormProps {
  userData: UserData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImageUpload: (base64: string | null) => void;
}

const CertificateForm: React.FC<CertificateFormProps> = ({ userData, onChange, onImageUpload }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageUpload(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Informative Header with User's specific Google Form link */}
        
      <div className="bg-[#00d4ff]/10 border border-[#00d4ff]/20 rounded-xl p-4 text-xs space-y-2">
          <p className="text-white/80 leading-relaxed">
            <span className="text-[#00d4ff] font-bold">Important:</span> The certificate will be fetched only if your <span className="underline">Email ID matches</span> the one used in the form filling.
          </p>
          <div className="flex items-center justify-between gap-2 pt-1 border-t border-white/5">
            <p className="text-white/40 italic">Haven't filled the form yet?</p>
            <a 
          href="https://docs.google.com/forms/d/e/1FAIpQLSeqjE1VdRf9vLkwYHOH7FiD4fE6pcnQ9OZFn5cUDTXjBRORJQ/viewform" 
          target="_blank" 
          rel="noopener noreferrer"
          className="relative z-10 flex items-center gap-1 text-[#00d4ff] hover:underline font-bold transition-all"
          onClick={(e) => e.stopPropagation()} // Adds protection against parent click events
        >
          Fill it now <ExternalLink size={12} />
        </a>
          </div>
        </div>
      
      <div className="space-y-2">
        <label htmlFor="fullName" className="text-xs font-futuristic text-cyan-400 tracking-widest uppercase">
          Full Name
        </label>
        <div className="relative group">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-[#00d4ff] transition-colors">
            <User size={18} />
          </div>
          <input
            type="text"
            id="fullName"
            name="fullName"
            placeholder="Enter your full name..."
            value={userData.fullName}
            onChange={onChange}
            autoComplete="off"
            className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-[#00d4ff]/50 focus:border-[#00d4ff] transition-all placeholder:text-white/20"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="email" className="text-xs font-futuristic text-cyan-400 tracking-widest uppercase">
          Email Address
        </label>
        <div className="relative group">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-[#00d4ff] transition-colors">
            <Mail size={18} />
          </div>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="name@example.com"
            value={userData.email}
            onChange={onChange}
            className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-[#00d4ff]/50 focus:border-[#00d4ff] transition-all placeholder:text-white/20"
          />
        </div>
      </div>

      {/* Screenshot Section - Scoped trigger for example image as requested */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-futuristic text-cyan-400 tracking-widest uppercase">
            Submit Screen Shote of submision form
          </label>
        </div>

        <div className="relative group/upload">
          {/* Example Tooltip - Only visible when hovering the upload area specifically */}
          <div className="absolute bottom-full left-0 right-0 mb-4 opacity-0 pointer-events-none group-hover/upload:opacity-100 transition-all duration-300 translate-y-2 group-hover/upload:translate-y-0 z-50">
            <div className="bg-[#0f172a]/95 backdrop-blur-xl border border-white/10 p-3 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.7)] max-w-[280px] mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle size={14} className="text-[#00d4ff]" />
                <p className="text-[9px] text-white font-bold uppercase tracking-wider">Required Submission Screen Shot</p>
              </div>
              <div className="overflow-hidden rounded-lg border border-white/5 bg-white/5">
                <img 
                  src="https://raw.githubusercontent.com/ChiragTankan/participants/refs/heads/main/components/122b0f06-7fdf-498d-90b9-749130ec7e33.jpg?token=GHSAT0AAAAAADRAATQJTFEPMYR5V4OWJ3G62MBWVQA" 
                  alt="Submission Confirmation Example" 
                  className="w-full h-auto object-cover opacity-90"
                />
              </div>
              <p className="text-[9px] text-white/40 mt-2 text-center italic">Example of correctly submitted form screenshot</p>
            </div>
            {/* Arrow */}
            <div className="w-3 h-3 bg-[#0f172a] border-r border-b border-white/10 rotate-45 mx-auto -mt-1.5 shadow-xl"></div>
          </div>

          <div 
            onClick={() => fileInputRef.current?.click()}
            className={`relative cursor-pointer border-2 border-dashed transition-all rounded-xl p-5 flex flex-col items-center justify-center gap-3 ${userData.submissionScreenshot ? 'border-[#00d4ff] bg-[#00d4ff]/10 shadow-[inset_0_0_20px_rgba(0,212,255,0.1)]' : 'border-white/10 hover:border-[#00d4ff]/50 bg-white/5 hover:bg-white/[0.07]'}`}
          >
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept="image/*" 
              className="hidden" 
            />
            
            {userData.submissionScreenshot ? (
              <div className="text-center animate-in fade-in zoom-in">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#00d4ff]/20 text-[#00d4ff] mb-2">
                  <ImageIcon size={20} />
                </div>
                <p className="text-[#00d4ff] font-bold text-xs mb-1 uppercase tracking-wider">Screenshot Attached</p>
                <p className="text-[10px] text-white/30">Click to change image</p>
              </div>
            ) : (
              <>
                <div className="p-3 rounded-full bg-white/5 group-hover/upload:bg-[#00d4ff]/10 transition-colors">
                  <Upload className="text-white/20 group-hover/upload:text-[#00d4ff] transition-colors" size={24} />
                </div>
                <div className="text-center">
                  <p className="text-xs text-white/40 font-bold uppercase tracking-widest group-hover/upload:text-white transition-colors">
                    Upload Submission Screenshot
                  </p>
                  <p className="text-[10px] text-white/20 mt-1">Hover to see example format</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateForm;
